// Story 7.35 (ADO-2032) — the pure /products URL-state contract.
//
// The address bar is the single source of truth for the catalogue view, so every
// shareable concern lives here as plain query parameters: `q` (the committed
// search term), `node` (the selected category UUID), the attribute facets
// (`brand`, `packSize`, ... from `FACET_KEYS`) and, for a category listing,
// `page` + `pageSize`. The screen reads its whole request out of these on every
// render and never keeps a second authoritative copy that could disagree with
// browser Back/Forward, a reload or a pasted URL (Decision 1).
//
// ── Parsing is total ──
// Nothing here throws. A hand-edited or stale URL answers with the sensible view,
// not an error: an unknown facet key is ignored, a `pageSize` outside the
// allow-list falls back to the default, a `page` below 1 clamps to 1, `q` is
// capped at 128 characters, and a `node` that is not a UUID is dropped (a
// syntactically valid but server-missing UUID is a separate concern the screen
// resolves from the listing response, Decision 8).
//
// ── Serialising is canonical ──
// A default is never written (page 1, the default size, an empty term/node all
// serialise to nothing), repeated owned keys collapse to their first value
// (Decision 4), and any parameter this screen does not own (a future campaign
// param, the Story 8.1 landing scope) is preserved untouched (Decision 6). While
// a search is active, `page`/`pageSize` are never written (search is a single
// capped page, Decision 7).
//
// ── Collections outlive a fresh listing ──
// Another node, another search term or another ordering entry starts a fresh listing and drops
// the facets that narrowed the old one. The offer facet (`offerType`) is the exception: it names
// a collection the shopper is sent to and shares (Story 7.30's `?offerType=LEAFLET_LINE`), so
// those moves narrow the collection rather than leave it. The collected-offers marker (Story
// 7.31's `offers=all`) and the landing scope (Story 8.1) are unowned params, kept the same way.

import {
  FACET_KEYS,
  appendFilterParams,
  splitFacetValues,
  type FacetKey,
  type ProductFilters,
} from '../../api/facets';
import { MAX_SEARCH_QUERY_LENGTH } from '../../api/search.client';
import { BROWSE_PAGE_SIZE_OPTIONS, DEFAULT_BROWSE_PAGE_SIZE } from '../Browse/browsePagination';

/** The owned query-parameter names, matching the API query vocabulary (Decision 3). */
export const PRODUCTS_PARAMS = {
  q: 'q',
  node: 'node',
  page: 'page',
  pageSize: 'pageSize',
} as const;

/** The term/facet-value character cap, shared with the search route (Decision 1.2). */
export const MAX_PRODUCTS_QUERY_LENGTH = MAX_SEARCH_QUERY_LENGTH;
/** Longest accepted single facet value; longer values are discarded (Decision 5). */
export const MAX_FACET_VALUE_LENGTH = 128;
/** Most values kept within one facet; the remainder are dropped (Decision 5). */
export const MAX_FACET_VALUES = 50;

/** The browse page-size allow-list the backend enforces; a size outside it is a
 *  400, so a URL carrying one falls back to the default rather than being sent. */
export const PRODUCTS_PAGE_SIZE_OPTIONS = BROWSE_PAGE_SIZE_OPTIONS;
/** The size the "Per page" control shows selected on first load. */
export const DEFAULT_PRODUCTS_PAGE_SIZE = DEFAULT_BROWSE_PAGE_SIZE;

/** Every owned key, used for canonical rewriting (repeated keys collapse). */
const OWNED_KEYS: readonly string[] = [
  PRODUCTS_PARAMS.q,
  PRODUCTS_PARAMS.node,
  PRODUCTS_PARAMS.page,
  PRODUCTS_PARAMS.pageSize,
  ...FACET_KEYS,
];

/** The canonical 8-4-4-4-12 hex UUID syntax the node param must match to be kept. */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The facets that name a collection rather than narrow one listing (see the header). Only
 *  unticking one, or Clear, leaves the collection. */
export const COLLECTION_FILTER_KEYS: readonly FacetKey[] = ['offerType'];

/** The no-facet selection: one frozen reference, so an empty selection is the same value on
 *  every parse (an effect that depends on it does not re-run on an unrelated URL change). */
export const NO_PRODUCT_FILTERS: ProductFilters = Object.freeze({});

/** The whole catalogue request the screen makes, as the URL holds it. */
export interface ProductsUrlState {
  /** The committed search term (trimmed, capped). Empty means "not searching". */
  readonly q: string;
  /** Whether a search is active (a non-empty `q`). */
  readonly searching: boolean;
  /** The selected category node UUID, or null for the root / search view. */
  readonly node: string | null;
  /** The active attribute filters (an absent/empty key imposes no constraint). */
  readonly filters: ProductFilters;
  /** The 1-based browse page (ignored while searching). */
  readonly page: number;
  /** The applied browse page size (ignored while searching). */
  readonly pageSize: number;
}

/** Read the catalogue view out of the query string. Total: never throws. */
export function parseProductsUrlState(params: URLSearchParams): ProductsUrlState {
  const q = parseQuery(params.get(PRODUCTS_PARAMS.q));
  return {
    q,
    searching: q !== '',
    node: parseNodeParam(params.get(PRODUCTS_PARAMS.node)),
    filters: parseProductFilters(params),
    page: parsePage(params.get(PRODUCTS_PARAMS.page)),
    pageSize: parsePageSize(params.get(PRODUCTS_PARAMS.pageSize)),
  };
}

/** The trimmed, 128-capped term. A hand-edited longer term is truncated, not rejected. */
function parseQuery(raw: string | null): string {
  return raw === null ? '' : normaliseSearchTerm(raw);
}

/**
 * The canonical form of a search term: trimmed, capped at 128 characters, then trimmed again,
 * so a cap that lands on a space leaves no trailing blank. A cap that splits a surrogate pair
 * drops the dangling half (the URL would otherwise encode it as U+FFFD). Idempotent, so a term
 * written through it is already canonical when it is read back. Every path that commits a term
 * (the catalogue box, the other screens' header search) goes through here.
 */
export function normaliseSearchTerm(raw: string): string {
  let capped = raw.trim().slice(0, MAX_PRODUCTS_QUERY_LENGTH);
  const last = capped.charCodeAt(capped.length - 1);
  if (last >= 0xd800 && last <= 0xdbff) capped = capped.slice(0, -1);
  return capped.trim();
}

/** A syntactically valid UUID in lower case, else null. Lower-casing gives one canonical URL
 *  per node (the server echoes ids in lower case), so an upper-case link is rewritten rather
 *  than kept as a second URL for the same view. A valid-but-missing UUID is NOT repaired
 *  here (Decision 8): the screen renders a category-not-found state from the 404. Story 7.33's
 *  plan view reads its `node` through here too (D-7.33-K), so both screens share one rule. */
export function parseNodeParam(raw: string | null): string | null {
  return raw !== null && UUID_PATTERN.test(raw) ? raw.toLowerCase() : null;
}

/** A safe whole number >= 1, else page 1. An over-range page is NOT clamped here: the
 *  server clamps it to the last page and the screen canonicalises to that
 *  (Decision 9), which keeps a bookmarked deep link working as the range changes. */
function parsePage(raw: string | null): number {
  if (raw === null) return 1;
  const page = Number(raw);
  // Safe integers only: the API rejects anything above Number.MAX_SAFE_INTEGER with a 400.
  return Number.isSafeInteger(page) && page >= 1 ? page : 1;
}

/** One of the allow-listed sizes, else the default. */
function parsePageSize(raw: string | null): number {
  if (raw === null) return DEFAULT_PRODUCTS_PAGE_SIZE;
  const size = Number(raw);
  return (PRODUCTS_PAGE_SIZE_OPTIONS as readonly number[]).includes(size)
    ? size
    : DEFAULT_PRODUCTS_PAGE_SIZE;
}

/** Read the active facets, iterating the `FACET_KEYS` allow-list only (so an
 *  unknown facet name can never enter `ProductFilters`), taking the first
 *  occurrence of a repeated key, and trimming / de-duplicating / bounding the
 *  comma-separated values (Decisions 4-6). */
export function parseProductFilters(params: URLSearchParams): ProductFilters {
  const filters: { -readonly [K in FacetKey]?: readonly string[] } = {};
  for (const key of FACET_KEYS) {
    const raw = params.get(key);
    if (raw === null) continue;
    const seen = new Set<string>();
    const values: string[] = [];
    // The writer escapes a value's comma (Story 7.29, D-7.29-34), so split on unescaped ones.
    for (const part of splitFacetValues(raw)) {
      const value = part.trim();
      if (value === '' || value.length > MAX_FACET_VALUE_LENGTH) continue;
      if (seen.has(value)) continue;
      seen.add(value);
      values.push(value);
      if (values.length >= MAX_FACET_VALUES) break;
    }
    if (values.length > 0) filters[key] = values;
  }
  return Object.keys(filters).length > 0 ? filters : NO_PRODUCT_FILTERS;
}

/**
 * Write the view back into a query string, preserving any parameter this screen
 * does not own (a campaign param, the Story 8.1 landing scope). Owned keys are
 * removed first so a repeated or stale owned key collapses to its canonical form;
 * defaults are omitted; `page`/`pageSize` are never written while searching.
 */
export function writeProductsUrlState(
  current: URLSearchParams,
  state: ProductsUrlState,
): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const key of OWNED_KEYS) next.delete(key);

  if (state.q !== '') next.set(PRODUCTS_PARAMS.q, state.q);
  if (state.node !== null) next.set(PRODUCTS_PARAMS.node, state.node);
  appendFilterParams(next, state.filters);
  if (!state.searching) writeBrowsePagination(next, state.page, state.pageSize);
  return next;
}

/**
 * Write a browse page and page size into a query string (in place), keeping every other
 * parameter: each is set when it is not the default and deleted when it is, so page 1 and the
 * default size never reach the URL. The one place that rule lives.
 */
export function writeBrowsePagination(
  params: URLSearchParams,
  page: number,
  pageSize: number,
): void {
  if (page > 1) params.set(PRODUCTS_PARAMS.page, String(page));
  else params.delete(PRODUCTS_PARAMS.page);
  if (pageSize !== DEFAULT_PRODUCTS_PAGE_SIZE) {
    params.set(PRODUCTS_PARAMS.pageSize, String(pageSize));
  } else params.delete(PRODUCTS_PARAMS.pageSize);
}

/**
 * Whether the owned parameters in `current` are already in their canonical form
 * for `state`. Order-independent and blind to non-owned keys, so it reports only
 * whether a sanitising rewrite is needed (a repeated owned key, a dropped invalid
 * value, a default left in the URL, pagination left under an active search). The
 * screen uses it to decide whether to replace the address bar, which avoids a
 * rewrite loop on an already-clean URL.
 */
export function isProductsSearchCanonical(
  current: URLSearchParams,
  state: ProductsUrlState,
): boolean {
  const canonical = writeProductsUrlState(new URLSearchParams(), state);
  for (const key of OWNED_KEYS) {
    const actual = current.getAll(key);
    const expected = canonical.getAll(key);
    if (actual.length !== expected.length) return false;
    for (let index = 0; index < actual.length; index += 1) {
      if (actual[index] !== expected[index]) return false;
    }
  }
  return true;
}

/**
 * The `/products` path for a search term or a node, for the screens that hand the shopper over
 * to the catalogue (another screen's header search, a breadcrumb, the mega-menu off-catalogue,
 * the favourites view). It writes through the same canonical serialiser, so the link it builds
 * is never rewritten on arrival.
 */
export function productsPath(view: { readonly q?: string; readonly node?: string | null }): string {
  const q = normaliseSearchTerm(view.q ?? '');
  const search = writeProductsUrlState(new URLSearchParams(), {
    q,
    searching: q !== '',
    node: view.node ?? null,
    filters: NO_PRODUCT_FILTERS,
    page: 1,
    pageSize: DEFAULT_PRODUCTS_PAGE_SIZE,
  }).toString();
  return search === '' ? '/products' : `/products?${search}`;
}

/** Drop the facets that narrowed one listing from a query string, keeping a collection's
 *  ({@link COLLECTION_FILTER_KEYS}): what a fresh listing (node, term, entry) starts from. */
export function dropListingFacets(params: URLSearchParams): void {
  for (const key of FACET_KEYS) {
    if (!COLLECTION_FILTER_KEYS.includes(key)) params.delete(key);
  }
}
